import { allows, consentStore } from "./consent.ts";

/**
 * Meta Pixel, for measuring Instagram/Facebook ads. It is optional analytics:
 * nothing loads until the visitor accepts analytics cookies, and events sent
 * before that are dropped, not queued.
 */
type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  push: Fbq;
  loaded: boolean;
  version: string;
};

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

let pixelId: string | null = null;
let currency = "EUR";
let loaded = false;

function load() {
  if (loaded || !pixelId || !allows("analytics")) return;
  loaded = true;
  // Meta's standard stub: queue calls until fbevents.js takes over callMethod.
  const fbq = ((...args: unknown[]) => {
    if (fbq.callMethod) fbq.callMethod(...args);
    else fbq.queue.push(args);
  }) as Fbq;
  Object.assign(fbq, { queue: [], push: fbq, loaded: true, version: "2.0" });
  window.fbq = fbq;
  window._fbq = fbq;
  const s = document.createElement("script");
  s.async = true;
  s.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(s);
  window.fbq("init", pixelId);
  window.fbq("track", "PageView");
}

/** Called once with the store config; loads now or as soon as the visitor accepts. */
export function initPixel(id: string | null | undefined, storeCurrency: string) {
  if (!id || pixelId) return;
  pixelId = id;
  currency = storeCurrency;
  load();
  consentStore.subscribe(() => {
    if (allows("analytics")) load();
    // Withdrawn consent: stop sending; the script itself stays until reload.
    else if (loaded) window.fbq?.("consent", "revoke");
  });
}

const send = (event: string, params?: Record<string, unknown>, eventId?: string) => {
  if (!loaded || !allows("analytics")) return;
  window.fbq?.("track", event, params, eventId ? { eventID: eventId } : undefined);
};

export const pixel = {
  pageView: () => send("PageView"),
  viewContent: (id: string, name: string, priceCents?: number) =>
    send("ViewContent", {
      content_ids: [id],
      content_name: name,
      content_type: "product",
      ...(priceCents != null && { value: priceCents / 100, currency }),
    }),
  addToCart: (id: string, name: string, valueCents: number) =>
    send("AddToCart", {
      content_ids: [id],
      content_name: name,
      content_type: "product",
      value: valueCents / 100,
      currency,
    }),
  initiateCheckout: (valueCents: number, items: number) =>
    send("InitiateCheckout", { value: valueCents / 100, currency, num_items: items }),
  /** The order id doubles as the event id, so a repeat never counts twice. */
  purchase: (orderId: string, valueCents: number, items: number) =>
    send("Purchase", { value: valueCents / 100, currency, num_items: items }, orderId),
};
